import React, { useEffect, useMemo, useState } from "react";
import {
  FaBuilding,
  FaMapMarkerAlt,
  FaSave,
  FaTimes,
  FaUniversity,
} from "react-icons/fa";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

const ClubModal = ({
  isOpen,
  onClose,
  onSubmit,
  club,
  demoMode = false,
  isSubmitting = false,
}) => {
  const [nomClub, setNomClub] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [departement, setDepartement] = useState("");
  const [adresse, setAdresse] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (club) {
      setNomClub(club.nom_club || "");
      setLogoUrl(club.logo_url || "");
      setDepartement(club.departement_club || "");
      setAdresse(club.adresse_club || "");
    } else {
      setNomClub("");
      setLogoUrl("");
      setDepartement("");
      setAdresse("");
    }
    setErrorMessage("");
  }, [club, isOpen]);

  const modalTitle = useMemo(() => {
    if (club) return demoMode ? "Consulter le club" : "Modifier le club";
    return "Créer un club";
  }, [club, demoMode]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (demoMode) {
      setErrorMessage(
        "Mode Démo : les modifications ne peuvent pas être enregistrées."
      );
      return;
    }

    if (!nomClub || !logoUrl || !departement || !adresse) {
      setErrorMessage("Tous les champs sont obligatoires.");
      return;
    }

    const clubData = {
      nom_club: nomClub.trim(),
      logo_url: logoUrl.trim(),
      departement_club: departement.trim(),
      adresse_club: adresse.trim(),
    };

    onSubmit(clubData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
      <div className="w-full max-w-3xl overflow-hidden rounded-[28px] border border-white/10 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900">
        <div className="grid lg:grid-cols-[0.9fr_1.1fr]">
          {/* Colonne gauche */}
          <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 p-6 text-white sm:p-8">
            <p className="inline-flex rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
              Votre club
            </p>

            <h2 className="mt-4 text-3xl font-black leading-tight">
              {modalTitle}
            </h2>

            <p className="mt-3 text-sm leading-7 text-white/80">
              Renseignez les informations essentielles de votre structure.
            </p>

            <div className="mt-8 flex flex-col items-center rounded-[24px] border  bg-white/10 p-5 text-center">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt="Aperçu du logo"
                  className="h-28 object-cover"
                />
              ) : (
                <div className="flex h-28 items-center justify-center rounded-full bg-white/10 text-white/70">
                  <FaBuilding className="text-5xl" />
                </div>
              )}

              <h3 className="mt-4 text-xl font-bold">
                {nomClub || "Nom du club"}
              </h3>
              <p className="mt-2 text-sm text-white/75">
                {departement || "Département"} · {adresse || "Adresse"}
              </p>

              {demoMode && (
                <div className="mt-4 rounded-full bg-amber-400/15 px-4 py-2 text-xs font-bold uppercase tracking-wide text-amber-200">
                  Mode Démo
                </div>
              )}
            </div>
          </div>

          {/* Colonne droite */}
          <div className="p-5 sm:p-6">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400">
                  Formulaire
                </p>
                <h3 className="text-xl font-black text-slate-900 dark:text-slate-100">
                  Informations du club
                </h3>
              </div>

              <button
                onClick={onClose}
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                <FaTimes />
              </button>
            </div>

            {errorMessage && (
              <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
                {errorMessage}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                  Nom du club
                </label>
                <div className="relative">
                  <FaUniversity className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                  <input
                    type="text"
                    value={nomClub}
                    onChange={(e) => setNomClub(e.target.value)}
                    className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                    placeholder="Nom officiel du club"
                    disabled={demoMode || isSubmitting}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                  Logo (URL)
                </label>
                <div className="relative">
                  <FaBuilding className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                  <input
                    type="text"
                    value={logoUrl}
                    onChange={(e) => setLogoUrl(e.target.value)}
                    className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                    placeholder="https://..."
                    disabled={demoMode || isSubmitting}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                  Département
                </label>
                <div className="relative">
                  <FaMapMarkerAlt className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                  <input
                    type="text"
                    value={departement}
                    onChange={(e) => setDepartement(e.target.value)}
                    className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                    placeholder="Ex : Gironde"
                    disabled={demoMode || isSubmitting}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                  Adresse
                </label>
                <textarea
                  value={adresse}
                  onChange={(e) => setAdresse(e.target.value)}
                  className="min-h-[110px] w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                  placeholder="Adresse complète du club"
                  disabled={demoMode || isSubmitting}
                  required
                />
              </div>

              <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-5 py-3 font-semibold text-slate-800 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                >
                  <FaTimes />
                  Fermer
                </button>

                <button
                  type="submit"
                  disabled={demoMode || isSubmitting}
                  className={cn(
                    "inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-3 font-semibold text-white transition",
                    demoMode || isSubmitting
                      ? "cursor-not-allowed bg-slate-400 dark:bg-slate-700"
                      : "bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
                  )}
                >
                  <FaSave />
                  {club ? "Enregistrer" : "Créer le club"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClubModal;